/* eslint-disable no-console */
import { DeviceIdProvider } from '@database/workspace';
import { DecodeJWTService } from '@domain/registration';
import { TranslateService } from '@ngx-translate/core';
import { ErrorCodes, FileModel, ToastDuration, ToastService } from '@shared';
import { LoggingService } from '@shared/logging';
import { RegistrationValidateTokenService } from '../../services/registration-validate-token.service';
import { IImportHandler } from '../import-handler.interface';
import { DecodeJWTResultModel } from '@domain/registration';
import jwt_decode from 'jwt-decode';

export class RegistrationValidatorHandler implements IImportHandler {
	private next: IImportHandler;

	public constructor(
		private loggingService: LoggingService,
		private translateService: TranslateService,
		private deviceIdProvider: DeviceIdProvider,
		private decodeJWTService: DecodeJWTService,
		private registrationValidateTokenService: RegistrationValidateTokenService,
		private toastService: ToastService,
	) { }

	public setNext(nextHandler: IImportHandler) {
		this.next = nextHandler;
	}

	public async handle(model: FileModel) {
		await this.checkRegistrationToken(model);
		await this.validateRegistrationToken(model);
		await this.checkDeviceId(model);

		if (this.next) {
			await this.next.handle(model);
		}
	}

	private async checkRegistrationToken(model: FileModel): Promise<void> {
		try {
			if (!model.FusionConfigHeader[0].registration_token) {
				throw new Error('No registration token found in the imported file');
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.FileValidatorHandlerNoRegistrationToken, error);
			await this.toastService.presentError(
				this.translateService.instant('alert.import.validationMessages.noRegistrationToken'),
				ToastDuration.Slow
			);
			throw error;
		}
	}

	private async validateRegistrationToken(model: FileModel): Promise<void> {
		try {
			if (!this.registrationValidateTokenService.validate(model.FusionConfigHeader[0].registration_token)) {
				throw new Error('Registration token is not valid.');
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.FileValidatorHandlerInvalidRegistrationToken, error);
			await this.toastService.presentError(
				this.translateService.instant('alert.import.validationMessages.invalidRegistrationToken'),
				ToastDuration.Slow
			);
			throw error;
		}
	}

	private async checkDeviceId(model: FileModel): Promise<void> {
		try {
			const storedDeviceId: string = await this.deviceIdProvider.getDeviceId();
			const deviceId = model.FusionConfigHeader[0].device_id;
			if (!storedDeviceId) {
				throw new Error('You do not have access to this file. Please try another one.');
			}

			const decodedToken: DecodeJWTResultModel = jwt_decode(model.FusionConfigHeader[0].registration_token);
			if (decodedToken.dev.length > 0 && decodedToken.dev.toString() != storedDeviceId) {
				throw new Error('Your device is not registered on this Workfile. Please try another one.');
			}
		} catch (error) {
			if (error.message.includes('not registered on this Workfile')) {
				this.loggingService.error(ErrorCodes.RegistrationDeviceIdError, error);
				await this.toastService.presentError(
					this.translateService.instant('alert.import.validationMessages.deviceNotRegistered'),
					ToastDuration.Slow
				);
			} else {
				this.loggingService.error(ErrorCodes.FileValidatorHandlerNoAccess, error);
				await this.toastService.presentError(
					this.translateService.instant('alert.import.validationMessages.noAccess'),
					ToastDuration.Slow
				);
			}
			throw error;
		}
	}
}
